import React, { useEffect, useMemo, useState } from "react";
import { apiRequest } from "../api/client.js";
import { AGE_BANDS, scoreMockAssessment } from "../scoring/scoring.js";
import {
  createLapAction,
  elapsedWholeSeconds,
  listQueuedLapActions,
  queueLapAction,
  removeQueuedLapAction,
  syncQueuedLapActions
} from "../stopwatch/offlineQueue.js";
import { ArrowLeft, Check, RotateCcw, Timer } from "../shared/icons.jsx";
import { Button, Field, MonoLabel, cx } from "../shared/ui.jsx";
import { mmss } from "../shared/dates.js";

const emptyScore = {
  ageBand: "",
  sexForScoring: "",
  bcaWhtr: "",
  strengthType: "pushup",
  strengthValue: "",
  coreType: "situp",
  coreValue: "",
  cardioType: "run",
  manualCardioValue: ""
};

const assessmentForm = (assessment) => assessment ? {
  ageBand: assessment.age_band || "",
  sexForScoring: assessment.sex_for_scoring || "",
  bcaWhtr: assessment.bca_whtr || "",
  strengthType: assessment.strength_type || "pushup",
  strengthValue: assessment.strength_value ?? "",
  coreType: assessment.core_type || "situp",
  coreValue: assessment.core_value || "",
  cardioType: assessment.cardio_type || "run",
  manualCardioValue: assessment.manual_cardio_value || ""
} : emptyScore;

function ToolShell({ title, subtitle, status, close, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-surface overflow-y-auto" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div className="w-full max-w-screen-sm mx-auto min-h-full flex flex-col">
        <div className="sticky top-0 z-10 bg-ink">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={close}
              className="flex items-center gap-2 bg-transparent border-0 text-white mono text-[11px] uppercase tracking-[0.12em] cursor-pointer p-1"
            >
              <ArrowLeft size={18} />Event
            </button>
            {status}
          </div>
          <div className="px-5 pb-4">
            <h2 className="display text-3xl leading-[0.9] text-white m-0">{title}</h2>
            {subtitle && <div className="mono text-[10px] uppercase tracking-[0.14em] text-muted mt-1">{subtitle}</div>}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

function ErrorBanner({ error }) {
  if (!error) return null;
  return (
    <div className="border border-[#c98b8b] bg-[rgba(176,86,58,0.1)] text-red-800 p-3 text-sm">{error}</div>
  );
}

function ScoreSummary({ score }) {
  if (!score) return <p className="text-sm text-muted m-0">Select an age band and sex for scoring.</p>;
  const items = [
    ["BCA", score.bca],
    ["Strength", score.strength],
    ["Core", score.core],
    ["Cardio", score.cardio],
    ["Composite", score.total]
  ];
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-line border border-line">
        {items.map(([label, value]) => (
          <div key={label} className="bg-white p-3">
            <MonoLabel className="text-[9px]">{label}</MonoLabel>
            <div className="display text-2xl text-ink mt-0.5">{typeof value === "number" ? value.toFixed(1) : "-"}</div>
          </div>
        ))}
      </div>
      {score.total !== null && (
        <div className={cx(
          "mt-2 p-3 mono text-xs font-semibold uppercase tracking-[0.1em]",
          score.passed ? "bg-[rgba(95,107,57,0.14)] text-pass-deep" : "bg-[rgba(176,86,58,0.14)] text-fail"
        )}>
          {score.passed ? "Passing mock score" : "Mock score does not meet minimums"}
        </div>
      )}
    </div>
  );
}

const fieldLabelClass = "block text-xs text-stone-600";

function AssessmentEditor({ registration, close, changed }) {
  const [assessment, setAssessment] = useState(null);
  const [form, setForm] = useState(emptyScore);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const result = await apiRequest("get_assessment", {
        query: { registrationId: registration.id }
      });
      setAssessment(result.assessment);
      setForm(assessmentForm(result.assessment));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, [registration.id]);

  const cardioValue = form.manualCardioValue ||
    (assessment?.stopwatch_dnf ? "DNF" : mmss(assessment?.stopwatch_cardio_seconds));
  const preview = useMemo(() => scoreMockAssessment({
    ...form,
    cardioValue
  }), [form, cardioValue]);

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await apiRequest("save_assessment", {
        method: "POST",
        body: { registrationId: registration.id, ...form }
      });
      setAssessment(result.assessment);
      setForm(assessmentForm(result.assessment));
      await changed();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const finalize = async () => {
    setBusy(true);
    setError("");
    try {
      await apiRequest("finalize_assessment", {
        method: "POST",
        body: { assessmentId: assessment.id }
      });
      await load();
      await changed();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const amend = async (voidScore = false) => {
    if (!reason.trim()) {
      setError("An amendment reason is required.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await apiRequest("amend_assessment", {
        method: "POST",
        body: { assessmentId: assessment.id, reason, void: voidScore, ...form }
      });
      setReason("");
      await load();
      await changed();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const finalized = assessment && assessment.status !== "draft";
  return (
    <ToolShell
      title="MOCK ASSESSMENT"
      subtitle={registration.staff_display_name || registration.participant_name}
      close={close}
    >
      <div className="flex-1 px-4 py-4 space-y-4">
        <ErrorBanner error={error} />
        {busy && !assessment ? <div className="text-muted mono text-xs uppercase tracking-[0.12em]">Loading score...</div> : (
          <>
            {assessment?.stopwatch_cardio_seconds !== null && assessment?.stopwatch_cardio_seconds !== undefined && (
              <div className="bg-white border-l-4 border-ink p-3 text-sm text-stone-600">
                <span className="mono text-[10px] font-semibold uppercase tracking-[0.12em] text-ink mr-2">Stopwatch</span>
                {mmss(Number(assessment.stopwatch_cardio_seconds))}
                {form.manualCardioValue && " (retained for audit; manual entry is current)"}
              </div>
            )}
            {assessment?.stopwatch_dnf === 1 && (
              <div className="bg-white border-l-4 border-fail p-3 text-sm text-stone-600">
                <span className="mono text-[10px] font-semibold uppercase tracking-[0.12em] text-fail mr-2">Stopwatch</span>
                DNF{form.manualCardioValue && " (manual entry is current)"}
              </div>
            )}
            <div className="bg-white p-4 grid sm:grid-cols-2 gap-3">
              <label className={fieldLabelClass}>Age band
                <Field as="select" disabled={busy} className="mt-1" value={form.ageBand} onChange={(e) => setForm({ ...form, ageBand: e.target.value })}>
                  <option value="">Select age band</option>
                  {AGE_BANDS.map((band) => <option key={band} value={band}>{band}</option>)}
                </Field>
              </label>
              <label className={fieldLabelClass}>Sex for scoring
                <Field as="select" disabled={busy} className="mt-1" value={form.sexForScoring} onChange={(e) => setForm({ ...form, sexForScoring: e.target.value })}>
                  <option value="">Select scoring column</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </Field>
              </label>
              <label className={fieldLabelClass}>Waist-to-height ratio
                <Field disabled={busy} className="mt-1" inputMode="decimal" placeholder="0.50" value={form.bcaWhtr} onChange={(e) => setForm({ ...form, bcaWhtr: e.target.value })} />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className={fieldLabelClass}>Strength
                  <Field as="select" disabled={busy} className="mt-1" value={form.strengthType} onChange={(e) => setForm({ ...form, strengthType: e.target.value })}>
                    <option value="pushup">Push-ups</option>
                    <option value="hr_pushup">Hand-release</option>
                  </Field>
                </label>
                <label className={fieldLabelClass}>Repetitions
                  <Field disabled={busy} className="mt-1" type="number" min="0" value={form.strengthValue} onChange={(e) => setForm({ ...form, strengthValue: e.target.value })} />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className={fieldLabelClass}>Core
                  <Field as="select" disabled={busy} className="mt-1" value={form.coreType} onChange={(e) => setForm({ ...form, coreType: e.target.value })}>
                    <option value="situp">Sit-ups</option>
                    <option value="clr_crunch">Cross-leg reverse crunch</option>
                    <option value="plank">Plank</option>
                  </Field>
                </label>
                <label className={fieldLabelClass}>{form.coreType === "plank" ? "Time (mm:ss)" : "Repetitions"}
                  <Field disabled={busy} className="mt-1" value={form.coreValue} onChange={(e) => setForm({ ...form, coreValue: e.target.value })} />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className={fieldLabelClass}>Cardio
                  <Field as="select" disabled={busy} className="mt-1" value={form.cardioType} onChange={(e) => setForm({ ...form, cardioType: e.target.value })}>
                    <option value="run">2-mile run</option>
                    <option value="hamr">20m HAMR</option>
                  </Field>
                </label>
                <label className={fieldLabelClass}>{form.cardioType === "run" ? "Manual time (mm:ss)" : "Shuttles"}
                  <Field disabled={busy} className="mt-1" placeholder={form.cardioType === "run" ? "Leave blank to use stopwatch" : ""} value={form.manualCardioValue} onChange={(e) => setForm({ ...form, manualCardioValue: e.target.value })} />
                </label>
              </div>
            </div>
            <ScoreSummary score={preview} />
            {!finalized ? (
              <div className="flex flex-wrap gap-2.5">
                <Button variant="outline" disabled={busy} onClick={save}>Save Draft</Button>
                <Button disabled={busy || !assessment || preview?.total == null} onClick={finalize}>
                  <Check size={14} />Finalize
                </Button>
              </div>
            ) : (
              <div className="bg-white p-4 space-y-3">
                <div className="flex gap-2 items-center">
                  <span className="mono text-[10px] font-semibold uppercase tracking-[0.1em] bg-ink text-white px-2 py-1">{assessment.status}</span>
                  <span className="mono text-[11px] text-muted">Revision {assessment.revision}</span>
                </div>
                <Field as="textarea" rows="2" placeholder="Required reason for amendment" value={reason} onChange={(e) => setReason(e.target.value)} />
                <div className="flex flex-wrap gap-2.5">
                  <Button disabled={busy} onClick={() => amend(false)}>Submit Amendment</Button>
                  <Button variant="danger" disabled={busy} onClick={() => amend(true)}>Void Score</Button>
                </div>
                {assessment.amendments?.length > 0 && (
                  <div>
                    <MonoLabel className="text-[9px] block mb-1">Amendment history</MonoLabel>
                    {assessment.amendments.map((item, index) => (
                      <div key={`${item.created_at}-${index}`} className="border-t border-line-soft py-2 text-sm">
                        <span className="text-ink">{item.reason}</span>
                        <span className="ml-2 text-muted mono text-[11px]">{item.authority} · {item.applied_as_current === 1 ? "current" : "retained"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </ToolShell>
  );
}

// The heat-operator UI (elapsed clock, runner laps, start form) without any
// surrounding chrome, so it can drop into both the standalone HeatConsole overlay
// and the guided WorkoutPlayer's lap segment. `defaultLaps` prefills the lap count.
export function HeatPanel({ event, changed, defaultLaps, title = "2-MILE STOPWATCH" }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [lapsRequired, setLapsRequired] = useState(defaultLaps ?? 8);
  const [now, setNow] = useState(Date.now());
  const [pending, setPending] = useState(0);
  const [localActions, setLocalActions] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const result = await apiRequest("event_heats", { query: { eventId: event.id } });
    setData(result);
    if (selected.length === 0) setSelected(result.registrations.map((item) => item.id));
    const queued = await listQueuedLapActions().catch(() => []);
    setPending(queued.length);
  };

  useEffect(() => {
    load().catch((err) => setError(err.message));
    const timer = setInterval(() => setNow(Date.now()), 500);
    const online = () => sync().catch((err) => setError(err.message));
    window.addEventListener("online", online);
    return () => {
      clearInterval(timer);
      window.removeEventListener("online", online);
    };
  }, [event.id]);

  const activeHeat = data?.heats.find((heat) => heat.status === "active");

  useEffect(() => {
    let lock = null;
    if (!activeHeat || !navigator.wakeLock?.request) return undefined;
    const acquire = async () => {
      try { lock = await navigator.wakeLock.request("screen"); } catch { /* best effort */ }
    };
    acquire();
    const visible = () => {
      if (document.visibilityState === "visible" && (!lock || lock.released)) acquire();
    };
    document.addEventListener("visibilitychange", visible);
    return () => {
      document.removeEventListener("visibilitychange", visible);
      lock?.release?.();
    };
  }, [activeHeat?.id]);

  const sendAction = async (action) => {
    const result = await apiRequest("lap_action", { method: "POST", body: action });
    if (result.heat) {
      setData((current) => ({
        ...current,
        heats: current.heats.map((heat) => heat.id === result.heat.id ? result.heat : heat)
      }));
    }
  };

  const sync = async () => {
    const count = await syncQueuedLapActions(sendAction);
    setPending(0);
    setLocalActions([]);
    if (count) await load();
  };

  const runAction = async (runner, actionType) => {
    if (!activeHeat?.canOperate) return;
    const action = createLapAction({
      heatId: activeHeat.id,
      heatRunnerId: runner.id,
      leaseVersion: activeHeat.leaseVersion,
      actionType,
      elapsedSeconds: elapsedWholeSeconds(activeHeat.startedAt, now)
    });
    setLocalActions((items) => [...items, action]);
    let queued = false;
    try {
      await queueLapAction(action);
      queued = true;
      setPending((count) => count + 1);
    } catch {
      setError("Offline storage is unavailable on this device. Keep this screen open until the lap syncs.");
    }
    try {
      await sendAction(action);
      if (queued) {
        await removeQueuedLapAction(action.id);
        setPending((count) => Math.max(0, count - 1));
      }
      setLocalActions((items) => items.filter((item) => item.id !== action.id));
    } catch (err) {
      if (!["network_error", "timeout"].includes(err.code)) {
        if (queued) {
          await removeQueuedLapAction(action.id).catch(() => {});
          setPending((count) => Math.max(0, count - 1));
        }
        setLocalActions((items) => items.filter((item) => item.id !== action.id));
      }
      setError(err.message);
    }
  };

  const start = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await apiRequest("start_heat", {
        method: "POST",
        body: { eventId: event.id, lapsRequired: Number(lapsRequired), registrationIds: selected }
      });
      setData((current) => ({ ...current, heats: [result.heat, ...current.heats] }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    setError("");
    try {
      await sync();
      const result = await apiRequest("stop_heat", {
        method: "POST",
        body: { heatId: activeHeat.id, leaseVersion: activeHeat.leaseVersion }
      });
      setData((current) => ({
        ...current,
        heats: current.heats.map((heat) => heat.id === result.heat.id ? result.heat : heat)
      }));
      await changed();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const takeover = async () => {
    setBusy(true);
    try {
      const result = await apiRequest("takeover_heat", {
        method: "POST",
        body: { heatId: activeHeat.id }
      });
      setData((current) => ({
        ...current,
        heats: current.heats.map((heat) => heat.id === result.heat.id ? result.heat : heat)
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const localDelta = (runnerId) => localActions
    .filter((action) => action.heatRunnerId === runnerId)
    .reduce((total, action) => total + (action.actionType === "lap" ? 1 : -1), 0);

  const syncStatus = (
    <span className={cx(
      "flex items-center gap-2 mono text-[10px] font-semibold uppercase tracking-[0.12em]",
      pending ? "text-warn" : "text-pass"
    )}>
      <span className={cx("w-[7px] h-[7px] rounded-full", pending ? "bg-warn" : "bg-pass")} />
      {pending ? `${pending} queued` : "Synced"}
    </span>
  );

  return (
    <>
      <div className="px-4 pt-3 flex justify-end">{syncStatus}</div>
      {!data ? (
          <div className="px-5 py-6 text-muted mono text-xs uppercase tracking-[0.12em]">Loading heat...</div>
        ) : activeHeat ? (
          <>
            {/* timer hero */}
            <div className="bg-ink px-5 pt-1 pb-6 text-center">
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted mb-1">{event.title} · Elapsed</div>
              <div className="display text-[88px] leading-[0.8] tracking-[0.02em] text-white tabular-nums">
                {mmss(elapsedWholeSeconds(activeHeat.startedAt, now))}
              </div>
              <div className="mono text-[11px] uppercase tracking-[0.14em] text-signal-bright mt-2.5">
                {activeHeat.lapsRequired} laps required · {activeHeat.runners.filter((runner) => runner.status === "finished").length} / {activeHeat.runners.length} finished
              </div>
              {!activeHeat.canOperate && activeHeat.canTakeOver && (
                <Button variant="dark" disabled={busy} onClick={takeover} className="w-full mt-4 border border-ink-line">
                  Take Over Heat
                </Button>
              )}
              {activeHeat.canOperate && (
                <button
                  disabled={busy}
                  onClick={stop}
                  className="mt-4 w-full h-[46px] bg-transparent border-[1.5px] border-[#5c3030] text-[#e07a7a] mono text-[11px] font-semibold uppercase tracking-[0.16em] cursor-pointer disabled:opacity-40"
                >
                  Stop &amp; mark remaining DNF
                </button>
              )}
            </div>

            <div className="flex-1 px-4 py-4 flex flex-col gap-3">
              <ErrorBanner error={error} />
              {!activeHeat.canOperate && (
                <div className="bg-white border-l-4 border-warn p-3 text-sm text-stone-600">
                  This heat is controlled by another staff device. An administrator can take it over.
                </div>
              )}
              {activeHeat.runners.map((runner) => {
                const lapCount = Math.max(0, Number(runner.lap_count) + localDelta(runner.id));
                const active = runner.status === "active";
                const finished = runner.status === "finished";
                return (
                  <div key={runner.id} className={cx("p-4", finished ? "bg-[#f3f4f6]" : "bg-white")}>
                    <div className="flex justify-between items-start gap-3 mb-3">
                      <div className="min-w-0">
                        <div className="text-base font-semibold text-ink truncate">{runner.runner_name_snapshot}</div>
                        <div className={cx(
                          "mono text-[9px] uppercase tracking-[0.12em] mt-0.5",
                          finished ? "text-pass-deep" : "text-muted"
                        )}>
                          {finished ? `Finished · ${mmss(runner.finish_seconds)}` : active ? "On course" : runner.status}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="display text-[38px] leading-[0.78] text-ink">{lapCount}/{activeHeat.lapsRequired}</div>
                        <div className="mono text-[8px] uppercase tracking-[0.14em] text-muted mt-0.5">Laps</div>
                      </div>
                    </div>
                    <div className="flex gap-2.5">
                      <button
                        disabled={!activeHeat.canOperate || !active}
                        onClick={() => runAction(runner, "lap")}
                        className={cx(
                          "flex-1 h-[54px] border-0 mono text-sm font-semibold uppercase tracking-[0.16em] flex items-center justify-center gap-2",
                          activeHeat.canOperate && active
                            ? "bg-signal text-ink cursor-pointer"
                            : "bg-[#e8eaec] text-muted cursor-not-allowed"
                        )}
                      >
                        <Timer size={18} />{active ? "Lap" : "Done"}
                      </button>
                      <button
                        disabled={!activeHeat.canOperate || lapCount === 0}
                        onClick={() => runAction(runner, "undo")}
                        aria-label="Undo last lap"
                        className="w-[54px] h-[54px] border-[1.5px] border-line bg-white text-stone-600 cursor-pointer flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <RotateCcw size={19} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <div className="bg-ink px-5 pt-1 pb-5">
              <h2 className="display text-3xl leading-[0.9] text-white m-0">{title}</h2>
              <div className="mono text-[10px] uppercase tracking-[0.14em] text-muted mt-1">{event.title}</div>
            </div>
            <div className="flex-1 px-4 py-4 space-y-4">
              <ErrorBanner error={error} />
              <div className="bg-white p-4">
                <label className={fieldLabelClass}>Manual lap count (1-50)
                  <Field className="mt-1 max-w-[10rem]" type="number" min="1" max="50" value={lapsRequired} onChange={(e) => setLapsRequired(e.target.value)} />
                </label>
              </div>
              <div>
                <MonoLabel className="text-[9px] block mb-2">Select runners</MonoLabel>
                <div className="bg-white">
                  {data.registrations.map((registration) => (
                    <label key={registration.id} className="px-4 py-3 border-b border-line-soft last:border-b-0 flex gap-3 items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-[#d4682a]"
                        checked={selected.includes(registration.id)}
                        onChange={(e) => setSelected(e.target.checked
                          ? [...selected, registration.id]
                          : selected.filter((id) => id !== registration.id))}
                      />
                      <span className="text-sm text-ink">
                        {registration.staff_display_name || registration.participant_name}
                        {registration.home_flight_name && <span className="mono text-[10px] text-muted ml-2">{registration.home_flight_name}</span>}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <Button size="lg" className="w-full" disabled={busy || selected.length === 0} onClick={start}>Start Heat</Button>
              {data.heats.length > 0 && (
                <div>
                  <MonoLabel className="text-[9px] block mb-2">Completed heats</MonoLabel>
                  <div className="bg-white">
                    {data.heats.filter((heat) => heat.status === "stopped").map((heat) => (
                      <div key={heat.id} className="px-4 py-2.5 border-b border-line-soft last:border-b-0 text-sm text-stone-600">
                        {new Date(heat.startedAt.replace(" ", "T")).toLocaleString()} · {heat.runners.length} runners · {heat.lapsRequired} laps
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
    </>
  );
}

// Standalone full-screen overlay wrapping HeatPanel with the "back to event" bar.
function HeatConsole({ event, close, changed }) {
  return (
    <div className="fixed inset-0 z-50 bg-surface overflow-y-auto" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div className="w-full max-w-screen-sm mx-auto min-h-full flex flex-col">
        <div className="sticky top-0 z-10 bg-ink flex items-center px-4 py-3">
          <button
            onClick={close}
            className="flex items-center gap-2 bg-transparent border-0 text-white mono text-[11px] uppercase tracking-[0.12em] cursor-pointer p-1"
          >
            <ArrowLeft size={18} />Event
          </button>
        </div>
        <HeatPanel event={event} changed={changed} />
      </div>
    </div>
  );
}

export default function MockTestTools({ event, registration, mode, close, changed }) {
  if (mode === "score" && registration) {
    return <AssessmentEditor registration={registration} close={close} changed={changed} />;
  }
  return <HeatConsole event={event} close={close} changed={changed} />;
}
